import React from 'react';
import {
  Card,
  CardContent,
  TextField,
  IconButton,
  Typography,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/**
 * 引導單元問卷卡片組件
 * @param {Object} node - 節點數據（content 為問卷 JSON 字串）
 * @param {number} index - 節點索引
 * @param {Function} onTitleChange - 標題變更回調 (index, title)
 * @param {Function} onContentChange - 內容變更回調 (index, jsonString)
 * @param {Function} onRemove - 刪除回調
 */
const InitialQuestionnaireCard = ({
  node,
  index,
  onTitleChange,
  onContentChange,
  onRemove,
}) => {
  let parsed = { description: '', questions: [{ id: 1, text: '' }] };
  try {
    if (node.content) parsed = JSON.parse(node.content);
  } catch (e) {
    console.error('Failed to parse questionnaire content:', e);
  }
  const questions = Array.isArray(parsed.questions) ? parsed.questions : [{ id: 1, text: '' }];
  const description = parsed.description || '';

  const updateContent = (newParsed) => {
    onContentChange(index, JSON.stringify(newParsed));
  };

  const handleDescriptionChange = (value) => {
    updateContent({ ...parsed, description: value });
  };

  const handleAddQuestion = () => {
    const ids = questions.map((q) => q.id).filter((id) => typeof id === 'number' && !isNaN(id));
    const newId = ids.length > 0 ? Math.max(...ids) + 1 : Date.now();
    updateContent({ ...parsed, questions: [...questions, { id: newId, text: '' }] });
  };

  const handleRemoveQuestion = (qIdx) => {
    updateContent({ ...parsed, questions: questions.filter((_, i) => i !== qIdx) });
  };

  const handleQuestionChange = (qIdx, value) => {
    updateContent({
      ...parsed,
      questions: questions.map((q, i) => (i === qIdx ? { ...q, text: value } : q)),
    });
  };

  return (
    <Card sx={{ position: 'relative' }}>
      <IconButton
        sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
        onClick={() => onRemove(index)}
        aria-label="刪除節點"
      >
        <DeleteIcon />
      </IconButton>
      <CardContent>
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              bgcolor: '#D1FAE5',
              color: '#065f46',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              fontSize: '11px',
            }}
          >
            📋 問卷節點
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="問卷標題"
          value={node.title || ''}
          onChange={(e) => onTitleChange(index, e.target.value)}
          sx={{ mb: 2 }}
          size="small"
          placeholder="輸入問卷標題"
        />

        <TextField
          fullWidth
          label="問卷描述（選填）"
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          sx={{ mb: 2 }}
          size="small"
          multiline
          rows={2}
          placeholder="說明這份問卷的目的或背景"
        />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          問題列表
        </Typography>

        {questions.map((q, qIdx) => (
          <Box key={q.id} display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>
              {qIdx + 1}.
            </Typography>
            <TextField
              value={q.text}
              onChange={(e) => handleQuestionChange(qIdx, e.target.value)}
              placeholder={`問題 ${qIdx + 1}`}
              variant="outlined"
              size="small"
              fullWidth
            />
            <IconButton
              onClick={() => handleRemoveQuestion(qIdx)}
              disabled={questions.length <= 1}
              size="small"
              color="error"
              aria-label="刪除問題"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddQuestion}
          size="small"
          variant="outlined"
          sx={{ mt: 0.5 }}
        >
          新增問題
        </Button>
      </CardContent>
    </Card>
  );
};

export default InitialQuestionnaireCard;
